import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  deleteFluxoMediaByUrl,
  deleteFluxoMediaFolder,
  isDataUrl,
  uploadFluxoMedia,
} from "@/lib/fluxoMedia";
import { supabase } from "@/lib/supabase";
import type { Database } from "@/lib/supabase";

export type FluxoBlockType = "texto" | "imagem" | "audio" | "delay" | "assistente";

export interface FluxoTextoBlock {
  id: string;
  type: "texto";
  content: string;
}

export interface FluxoImagemBlock {
  id: string;
  type: "imagem";
  url: string;
  fileName?: string;
}

export interface FluxoAudioBlock {
  id: string;
  type: "audio";
  url: string;
  fileName?: string;
}

export interface FluxoDelayBlock {
  id: string;
  type: "delay";
  seconds: number;
}

export interface FluxoAssistenteBlock {
  id: string;
  type: "assistente";
  assistantId: string;
}

export type FluxoBlock =
  | FluxoTextoBlock
  | FluxoImagemBlock
  | FluxoAudioBlock
  | FluxoDelayBlock
  | FluxoAssistenteBlock;

export interface FluxoSettings {
  webhookEnabled: boolean;
  webhookToken: string;
}

export interface Fluxo {
  id: string;
  name: string;
  blocks: FluxoBlock[];
  settings?: FluxoSettings;
  createdAt: string;
  updatedAt: string;
}

type AccountFluxoRow = Database["public"]["Tables"]["account_fluxos"]["Row"];

const LOCAL_STORAGE_KEY = "rolt_fluxos";
const migratedKeyFor = (accountId: string) => `rolt_fluxos_migrated_${accountId}`;

export const generateId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export const generateWebhookToken = (): string => generateId();

export const createEmptyBlock = (type: FluxoBlockType): FluxoBlock => {
  const id = generateId();
  switch (type) {
    case "texto":
      return { id, type, content: "" };
    case "imagem":
      return { id, type, url: "" };
    case "audio":
      return { id, type, url: "" };
    case "delay":
      return { id, type, seconds: 5 };
    case "assistente":
      return { id, type, assistantId: "" };
  }
};

const isFluxoBlock = (value: unknown): value is FluxoBlock => {
  if (!value || typeof value !== "object") return false;
  const block = value as Record<string, unknown>;
  return typeof block.id === "string" && typeof block.type === "string";
};

const parseBlocks = (raw: unknown): FluxoBlock[] => {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isFluxoBlock);
};

const rowToFluxo = (row: AccountFluxoRow): Fluxo => ({
  id: row.id,
  name: row.name,
  blocks: parseBlocks(row.blocks),
  settings: {
    webhookEnabled: row.webhook_enabled,
    webhookToken: row.webhook_token || generateWebhookToken(),
  },
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

const readLocalFluxos = (): Fluxo[] => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item): item is Fluxo =>
        item &&
        typeof item === "object" &&
        typeof item.id === "string" &&
        typeof item.name === "string" &&
        Array.isArray(item.blocks)
    );
  } catch {
    return [];
  }
};

const clearLocalFluxos = (accountId: string): void => {
  try {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    localStorage.setItem(migratedKeyFor(accountId), "1");
  } catch {
    // ignore quota / private mode
  }
};

const wasMigrated = (accountId: string): boolean => {
  try {
    return localStorage.getItem(migratedKeyFor(accountId)) === "1";
  } catch {
    return false;
  }
};

/**
 * Converte data URLs de mídia legada para o bucket fluxo-media antes de
 * persistir o fluxo migrado.
 */
const migrateBlockMedia = async (
  accountId: string,
  fluxoId: string,
  blocks: FluxoBlock[]
): Promise<FluxoBlock[]> => {
  const next: FluxoBlock[] = [];

  for (const block of blocks) {
    if ((block.type === "imagem" || block.type === "audio") && isDataUrl(block.url)) {
      try {
        const blob = await (await fetch(block.url)).blob();
        const publicUrl = await uploadFluxoMedia({
          accountId,
          fluxoId,
          blockId: block.id,
          file: blob,
          kind: block.type,
          fileName: block.fileName,
        });
        next.push({ ...block, url: publicUrl });
      } catch (err) {
        console.warn("⚠️ [useFluxos] Mídia legada ignorada na migração:", err);
        next.push({ ...block, url: "" });
      }
    } else {
      next.push(block);
    }
  }

  return next;
};

interface UseFluxosReturn {
  fluxos: Fluxo[];
  loading: boolean;
  error: string | null;
  getFluxo: (id: string) => Fluxo | undefined;
  createFluxo: (
    data: Pick<Fluxo, "name" | "blocks"> &
      Partial<Pick<Fluxo, "settings" | "id">>
  ) => Promise<Fluxo | null>;
  updateFluxo: (
    id: string,
    data: Partial<Pick<Fluxo, "name" | "blocks" | "settings">>
  ) => Promise<boolean>;
  deleteFluxo: (id: string) => Promise<boolean>;
  refetch: () => Promise<void>;
}

export const useFluxos = (): UseFluxosReturn => {
  const { user } = useAuth();
  const [fluxos, setFluxos] = useState<Fluxo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const migratingRef = useRef(false);

  const fetchFluxos = useCallback(async (): Promise<Fluxo[]> => {
    if (!user?.account_id || !supabase) {
      setFluxos([]);
      setLoading(false);
      return [];
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from("account_fluxos")
        .select("*")
        .eq("account_id", user.account_id)
        .order("updated_at", { ascending: false });

      if (fetchError) throw fetchError;

      const mapped = (data || []).map(rowToFluxo);
      setFluxos(mapped);
      return mapped;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Erro ao carregar fluxos";
      setError(message);
      return [];
    } finally {
      setLoading(false);
    }
  }, [user?.account_id]);

  const migrateFromLocalStorage = useCallback(
    async (existing: Fluxo[]): Promise<void> => {
      if (!user?.account_id || !supabase || migratingRef.current) return;
      if (wasMigrated(user.account_id)) return;

      const local = readLocalFluxos();
      if (local.length === 0) {
        clearLocalFluxos(user.account_id);
        return;
      }

      migratingRef.current = true;
      const existingIds = new Set(existing.map((f) => f.id));
      let imported = 0;

      try {
        for (const localFluxo of local) {
          if (existingIds.has(localFluxo.id)) continue;

          const fluxoId = localFluxo.id || generateId();
          const blocks = await migrateBlockMedia(
            user.account_id,
            fluxoId,
            localFluxo.blocks
          );

          const { error: insertError } = await supabase.from("account_fluxos").insert({
            id: fluxoId,
            account_id: user.account_id,
            name: localFluxo.name || "Fluxo migrado",
            blocks,
            webhook_enabled: localFluxo.settings?.webhookEnabled ?? false,
            webhook_token: localFluxo.settings?.webhookToken || generateWebhookToken(),
            created_at: localFluxo.createdAt || new Date().toISOString(),
            updated_at: localFluxo.updatedAt || new Date().toISOString(),
          });

          if (insertError) {
            console.error("💥 [useFluxos] Falha ao migrar fluxo:", insertError);
            continue;
          }

          imported += 1;
        }

        clearLocalFluxos(user.account_id);

        if (imported > 0) {
          toast.success(
            `${imported} ${imported === 1 ? "fluxo migrado" : "fluxos migrados"} do navegador`
          );
          await fetchFluxos();
        }
      } catch (err) {
        console.error("💥 [useFluxos] Erro na migração localStorage:", err);
      } finally {
        migratingRef.current = false;
      }
    },
    [user?.account_id, fetchFluxos]
  );

  useEffect(() => {
    if (!user?.account_id) {
      setFluxos([]);
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      const existing = await fetchFluxos();
      if (!cancelled) {
        await migrateFromLocalStorage(existing);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.account_id, fetchFluxos, migrateFromLocalStorage]);

  const getFluxo = useCallback(
    (id: string): Fluxo | undefined => fluxos.find((f) => f.id === id),
    [fluxos]
  );

  const createFluxo = useCallback(
    async (
      data: Pick<Fluxo, "name" | "blocks"> & Partial<Pick<Fluxo, "settings" | "id">>
    ): Promise<Fluxo | null> => {
      if (!user?.account_id || !supabase) {
        toast.error("Sessão inválida para salvar o fluxo");
        return null;
      }

      const id = data.id || generateId();
      const webhookToken = data.settings?.webhookToken || generateWebhookToken();
      const webhookEnabled = data.settings?.webhookEnabled ?? false;

      const { data: row, error: insertError } = await supabase
        .from("account_fluxos")
        .insert({
          id,
          account_id: user.account_id,
          name: data.name,
          blocks: data.blocks,
          webhook_enabled: webhookEnabled,
          webhook_token: webhookToken,
        })
        .select("*")
        .single();

      if (insertError || !row) {
        toast.error(insertError?.message || "Erro ao criar fluxo");
        return null;
      }

      const created = rowToFluxo(row);
      setFluxos((prev) => [created, ...prev.filter((f) => f.id !== created.id)]);
      return created;
    },
    [user?.account_id]
  );

  const updateFluxo = useCallback(
    async (
      id: string,
      data: Partial<Pick<Fluxo, "name" | "blocks" | "settings">>
    ): Promise<boolean> => {
      if (!user?.account_id || !supabase) {
        toast.error("Sessão inválida para salvar o fluxo");
        return false;
      }

      const patch: Database["public"]["Tables"]["account_fluxos"]["Update"] = {};
      if (data.name !== undefined) patch.name = data.name;
      if (data.blocks !== undefined) patch.blocks = data.blocks;
      if (data.settings !== undefined) {
        patch.webhook_enabled = data.settings.webhookEnabled;
        patch.webhook_token = data.settings.webhookToken;
      }

      const { data: row, error: updateError } = await supabase
        .from("account_fluxos")
        .update(patch)
        .eq("id", id)
        .eq("account_id", user.account_id)
        .select("*")
        .single();

      if (updateError || !row) {
        toast.error(updateError?.message || "Erro ao atualizar fluxo");
        return false;
      }

      const updated = rowToFluxo(row);
      setFluxos((prev) => prev.map((f) => (f.id === id ? updated : f)));
      return true;
    },
    [user?.account_id]
  );

  const deleteFluxo = useCallback(
    async (id: string): Promise<boolean> => {
      if (!user?.account_id || !supabase) {
        toast.error("Sessão inválida para excluir o fluxo");
        return false;
      }

      const existing = fluxos.find((f) => f.id === id);

      const { error: deleteError } = await supabase
        .from("account_fluxos")
        .delete()
        .eq("id", id)
        .eq("account_id", user.account_id);

      if (deleteError) {
        toast.error(deleteError.message || "Erro ao excluir fluxo");
        return false;
      }

      setFluxos((prev) => prev.filter((f) => f.id !== id));

      if (existing) {
        for (const block of existing.blocks) {
          if (block.type === "imagem" || block.type === "audio") {
            await deleteFluxoMediaByUrl(block.url);
          }
        }
        await deleteFluxoMediaFolder(user.account_id, id);
      }

      return true;
    },
    [user?.account_id, fluxos]
  );

  const refetch = useCallback(async () => {
    await fetchFluxos();
  }, [fetchFluxos]);

  return {
    fluxos,
    loading,
    error,
    getFluxo,
    createFluxo,
    updateFluxo,
    deleteFluxo,
    refetch,
  };
};
